export default {
    title: 'Profile',
    subtitle: 'Manage your personal information and preferences',
    tabs: {
        preferences: 'Preferences',
        account: 'Account',
        password: 'Password',
        picture: 'Profile Picture',
        companies: 'Companies',
        team: 'Team',
        integrations: 'Integrations',
    },
    preferences: {
        language: {
            title: 'Language',
            description: 'Select your preferred language for the interface',
        },
        theme: {
            title: 'Theme',
            description: 'Choose between light and dark theme',
        },
    },
    account: {
        title: 'Account Information',
        description: 'Update your personal information',
        legalSection: {
            title: 'Legal',
            description: 'View our Terms & Conditions and Privacy Policy.',
        },
        firstName: 'First Name',
        firstNamePlaceholder: 'Enter your first name',
        lastName: 'Last Name',
        lastNamePlaceholder: 'Enter your last name',
        email: 'Email',
        emailPlaceholder: 'Enter your email address',
        phone: 'Phone',
        phonePlaceholder: 'Enter your phone number',
        jobTitle: 'Job Title',
        jobTitlePlaceholder: 'Enter your job title',
        department: 'Department',
        departmentPlaceholder: 'Enter your department',
    },
    password: {
        title: 'Change Password',
        description: 'Update your account password',
        currentPassword: 'Current Password',
        currentPasswordPlaceholder: 'Enter your current password',
        newPassword: 'New Password',
        newPasswordPlaceholder: 'Enter your new password',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Confirm your new password',
        mismatch: 'Passwords do not match',
        changePassword: 'Change Password',
    },
    picture: {
        title: 'Profile Picture',
        description: 'Upload and manage your profile picture',
        global: 'Global (All Companies)',
        scope: 'Scope',
        scopeDescription: 'Choose whether this picture should be visible globally or only for a specific company',
        selectFile: 'Select File',
        upload: 'Upload',
        invalidFileType: 'Invalid file type. Please select a PNG, JPG, JPEG, or WEBP image.',
        fileTooLarge: 'File size exceeds 5MB limit.',
    },
    companies: {
        title: 'Companies',
        description: 'View all companies you are a member of',
        noCompanies: 'You are not a member of any companies',
        primary: 'Primary',
        noType: 'No Type',
        types: {
            sender: 'Sender',
            receiver: 'Receiver',
            freight_forwarder: 'Freight Forwarder',
            port_authority: 'Port Authority',
            carrier: 'Carrier',
            customs_broker: 'Customs Broker',
            insurance_provider: 'Insurance Provider',
        },
        role: {
            super_admin: 'Super Admin',
            company_admin: 'Company Admin',
            operator: 'Operator',
            viewer: 'Viewer',
        },
        editDetails: 'Company details',
    },
    companyEdit: {
        nav: {
            companyInfo: 'Company info',
            hsCodes: 'HS codes',
            routes: 'Routes',
            carriers: 'Carriers',
            ariaLabel: 'Company profile sections',
        },
        title: 'Company profile',
        back: 'Back to companies',
        save: 'Save changes',
        saved: 'Company profile saved',
        saveError: 'Could not save company profile',
        loadError: 'Could not load company data',
        notMember: 'You are not a member of this company',
        readOnlyHint:
            'Only company administrators can edit these details. Contact an admin if something needs to be updated.',
        retry: 'Retry',
        operationalProfile: {
            title: 'Operational profile',
            description:
                'Declare your main HS codes, trade routes, and preferred carriers. This completes your shipper profile for the Control Hub checklist and gives Ask Eve richer company context.',
            hsHeading: 'Products (HS codes)',
            hsLabel: 'HS code',
            hsSearchLabel: 'Search HS codes',
            hsSearchPlaceholder: 'Type keywords or digits (e.g. avocado, 0804)',
            hsPlaceholder: 'e.g. 080440',
            hsInvalid: 'Enter a valid HS code (digits and dots, min. 4 characters).',
            add: 'Add',
            hsEmpty: 'No HS codes added yet.',
            hsAlreadyDeclared: 'This HS code is already in your list.',
            hsAddCodesButton: 'Add HS codes',
            hsTableFilterHint: 'Filter the table below — use the button above to add new codes.',
            removeHs: 'Remove {{code}}',
            routesHeading: 'Main trade routes',
            addRoute: 'Add route',
            routesEmpty: 'No routes added yet.',
            routesIncomplete: 'Complete each route (origin and destination port), or remove incomplete rows.',
            legacyRouteHint: 'Saved as countries: {{origin}} → {{dest}}. Select ports below to update.',
            origin: 'Origin country',
            destination: 'Destination country',
            originPort: 'Origin port',
            destinationPort: 'Destination port',
            portSearchPlaceholder: 'Search port name or UN/LOCODE',
            routeNote: 'Note (optional)',
            removeRoute: 'Remove route',
            carriersHeading: 'Carriers you use most',
            carrierSelectLabel: 'Add carrier',
            carrierSearchLabel: 'Search carriers',
            carrierSearchPlaceholder: 'Type name or SCAC',
            carrierPlaceholder: 'Select a carrier',
            addCarrier: 'Add carrier',
            carriersEmpty: 'No carriers added yet.',
            carriersAddButton: 'Add carriers',
            carriersTableFilterHint:
                'Filter the table below — use the button above to add carriers.',
            carrierAlreadyDeclared: 'This carrier is already in your list.',
            carrierModalTitle: 'Add carriers',
            carrierModalDescription:
                'Search and select one or more ocean carriers. They will be added when you confirm.',
            carrierModalStagingEmpty: 'No carriers selected yet — search above to add.',
            carrierModalCancel: 'Cancel',
            carrierModalConfirm: 'Add to list',
            removeCarrier: 'Remove {{scac}}',
            hsTableSearchPlaceholder: 'Search this list…',
            hsTableTitle: 'Declared HS codes',
            hsCodeColumn: 'Code',
            hsDescriptionColumn: 'Description',
            tableActions: 'Actions',
            routesTableSearchPlaceholder: 'Search routes…',
            routesTableTitle: 'Routes',
            routesTableHint: 'Expand a row to edit document arrangements and upload PDFs.',
            carriersTableSearchPlaceholder: 'Search carriers…',
            carriersTableTitle: 'Carriers',
            hsModalTitle: 'Add HS codes',
            hsModalDescription:
                'Search and select one or more codes. They will be added when you confirm.',
            hsModalStagingEmpty: 'No codes selected yet — search above to add.',
            hsModalCancel: 'Cancel',
            hsModalConfirm: 'Add to list',
            carrierColumnScac: 'SCAC',
            carrierColumnName: 'Name',
            carrierColumnCountry: 'Country',
            saveOperational: 'Save operational profile',
            saved: 'Operational profile saved',
            saveError: 'Could not save operational profile',
            loadError: 'Could not load operational profile',
        },
    },
    integrations: {
        title: 'Email Integrations',
        description: 'Connect your personal Gmail or Outlook accounts to Eve Mind',
        connectedAccounts: 'Connected Accounts',
        connectNew: 'Connect New Account',
        connectGmail: 'Connect Gmail',
        connectOutlook: 'Connect Outlook',
        confirmDisconnect: 'Are you sure you want to disconnect this email account?',
        providers: {
            gmail: 'Gmail',
            outlook: 'Outlook',
        },
        status: {
            active: 'Active',
            inactive: 'Inactive',
            error: 'Error',
            authentication_required: 'Authentication Required',
        },
        tracking: {
            title: 'Container Tracking',
            description: 'Configure automatic tracking updates for your shipments',
            autoRefresh: 'Automatic Updates',
            autoRefreshDescription: 'Enable automatic tracking status updates',
            dailyUpdates: 'Daily Updates',
            dailyUpdatesDescription: 'Number of tracking updates per day',
            updateTime: 'Update Time (UTC)',
            updateTimeDescription: 'Time when daily tracking update runs',
            saveSuccess: 'Tracking settings saved successfully',
            saveError: 'Failed to save tracking settings',
            loadError: 'Failed to load tracking settings',
        },
    },
};

